import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing } from "lit";
import { property, query } from "lit/decorators.js";
import { pdIcons } from "@progressive-development/pd-icon";
import "@progressive-development/pd-icon/pd-icon";
//#region src/pd-popup/PdPopup.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/**
* Modal popup triggered by clicking a slot element.
*
* Rendert intern ein natives `<dialog>`, geöffnet via `showModal()`.
* Das Popup liegt damit im Top-Layer des Browsers und ist immun gegen
* Containing Blocks von Vorfahren (`transform`, `filter`, `contain`),
* `overflow`-Clipping und z-index-Stacking. `showModal()` liefert
* außerdem Fokus-Trap, `inert`-Hintergrund und Fokus-Rückgabe beim
* Schließen.
*
* @tagname pd-popup
* @summary Modal popup with trigger element and content slot.
*
* @event popup-close - Fired when popup is closed.
*
* @slot small-view - Trigger content (e.g., icon, text).
* @slot content - Content displayed in the popup.
*
* @cssprop --pd-popup-modal-bg-rgba - Backdrop background (`::backdrop`). Default: `--pd-modal-overlay-col`.
* @cssprop --pd-popup-modal-padding - Modal content padding. Default: `--pd-spacing-md`.
* @cssprop --pd-popup-modal-padding-top - Top padding for vertical positioning. Default: `100px`.
* @cssprop --pd-popup-modal-padding-bottom - Bottom padding inside content. Default: `130px`.
* @cssprop --pd-popup-max-width - Maximum popup width. Default: `1200px`.
* @cssprop --pd-popup-width - Popup width. Default: `80%`.
* @cssprop --pd-popup-border-radius - Corner radius. Default: `--pd-radius-lg`.
* @cssprop --pd-popup-modal-slot-max-width - Max width of slot content. Default: `1000px`.
* @cssprop --pd-popup-modal-slot-margin - Slot content margin. Default: `0 --pd-spacing-lg`.
* @cssprop --pd-popup-content-bg - Modal content background color. Default: `--pd-default-bg-col`.
* @cssprop --pd-popup-content-height - Modal content height. Default: `auto`.
*/
var PdPopup = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.startOpen = false;
		this.closeByEscape = false;
		this.closeByBackdrop = false;
		this.hideCloseIcon = false;
		this.blockScroll = false;
		this.popupLabel = "Popup";
		this._scrollBlocked = false;
	}
	static {
		this.styles = [css`
      :host {
        display: block;
      }

      /* Der Dialog selbst ist ein transparenter Vollbild-Scrollcontainer
       * (früher .modal). Klicks neben .modal-content treffen so das
       * Dialog-Element → Backdrop-Close. Die UA-Defaults max-width/
       * max-height (calc(100% - 6px - 2em)) müssen explizit aufgehoben
       * werden. */
      dialog {
        position: fixed;
        inset: 0;
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        max-width: none;
        max-height: none;
        border: none;
        margin: 0;
        padding: 0;
        padding-top: var(--pd-popup-modal-padding-top, 100px);
        background: transparent;
        /* Das UA-Stylesheet malt ein dialog-Element in CanvasText und
           unterbricht damit die Vererbung: Eine App, die ihre Textfarbe
           einmal am Host setzt, bekommt im Dialog trotzdem Schwarz — und
           kann es von außen nicht korrigieren, weil die Regel in diesem
           Shadow Root steht. */
        color: inherit;

        overflow: auto;
        overflow-x: hidden;
      }

      /* Custom Properties erben seit Baseline 2024 in ::backdrop. */
      dialog::backdrop {
        background-color: var(
          --pd-popup-modal-bg-rgba,
          var(--pd-modal-overlay-col)
        );
      }

      .modal-content {
        background-color: var(--pd-popup-content-bg, var(--pd-default-bg-col));
        margin: auto;
        padding: var(--pd-popup-modal-padding, var(--pd-spacing-md));
        padding-bottom: var(--pd-popup-modal-padding-bottom, 130px);
        border: var(--pd-border-width, 1px) solid var(--pd-default-col);
        border-radius: var(--pd-popup-border-radius, var(--pd-radius-lg));
        box-shadow: var(--pd-shadow-xl);
        width: var(--pd-popup-width, 80%);
        max-width: var(--pd-popup-max-width, 1200px);
        height: var(--pd-popup-content-height, auto);
        position: relative;
      }

      .modal-content-slot {
        max-width: var(--pd-popup-modal-slot-max-width, 1000px);
        margin: var(--pd-popup-modal-slot-margin, 0 var(--pd-spacing-lg, 2rem));
        height: 100%;
      }

      .close-icon {
        position: absolute;
        cursor: pointer;
        right: var(--pd-spacing-md, 1rem);
        top: var(--pd-spacing-md, 1rem);
      }

      .close-icon:focus {
        outline: 2px solid var(--pd-default-col);
        outline-offset: 2px;
        border-radius: var(--pd-radius-sm, 2px);
      }
    `];
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this._unblockScroll();
	}
	firstUpdated() {
		if (this.startOpen) this.showPopup();
	}
	/**
	* Öffnet das Popup programmatisch
	*/
	showPopup() {
		if (!this._dialog || this._dialog.open) return;
		this._dialog.showModal();
		if (this.blockScroll && !this._scrollBlocked) {
			document.body.style.overflow = "hidden";
			this._scrollBlocked = true;
		}
	}
	/**
	* Schließt das Popup programmatisch
	*/
	hidePopup() {
		this._dialog?.close();
	}
	render() {
		const popupId = `${this.id || "popup"}-content`;
		return html`
      <span @click=${this.showPopup} @keypress=${this.showPopup}>
        <slot name="small-view"></slot>
      </span>

      <dialog
        id="modalId"
        aria-label="${this.popupLabel}"
        @cancel=${this._onCancel}
        @close=${this._onDialogClose}
        @click=${this._onBackdropClick}
      >
        <div class="modal-content">
          ${this.hideCloseIcon ? nothing : html`
                  <pd-icon
                    icon=${pdIcons.ICON_CLOSE}
                    class="close-icon"
                    tabindex="0"
                    role="button"
                    aria-label="Close popup"
                    @click=${this.hidePopup}
                    @keydown=${this._onCloseIconKeyDown}
                  ></pd-icon>
                `}
          <div id="${popupId}" class="modal-content-slot">
            <slot name="content"></slot>
          </div>
        </div>
      </dialog>
    `;
	}
	/** ESC löst auf dem Dialog ein cancel aus — nur zulassen, wenn erlaubt. */
	_onCancel(e) {
		if (!this.closeByEscape) e.preventDefault();
	}
	_onBackdropClick(e) {
		if (this.closeByBackdrop && e.target === this._dialog) this._dialog.close();
	}
	/** Zentraler Schließ-Pfad: feuert bei ESC, Backdrop und close(). */
	_onDialogClose() {
		this._unblockScroll();
		this.dispatchEvent(new CustomEvent("popup-close", {
			bubbles: true,
			composed: true
		}));
	}
	_unblockScroll() {
		if (this._scrollBlocked) {
			document.body.style.overflow = "";
			this._scrollBlocked = false;
		}
	}
	_onCloseIconKeyDown(e) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			this.hidePopup();
		}
	}
};
__decorate([property({ type: Boolean })], PdPopup.prototype, "startOpen", void 0);
__decorate([property({ type: Boolean })], PdPopup.prototype, "closeByEscape", void 0);
__decorate([property({ type: Boolean })], PdPopup.prototype, "closeByBackdrop", void 0);
__decorate([property({ type: Boolean })], PdPopup.prototype, "hideCloseIcon", void 0);
__decorate([property({ type: Boolean })], PdPopup.prototype, "blockScroll", void 0);
__decorate([property({ type: String })], PdPopup.prototype, "popupLabel", void 0);
__decorate([query("#modalId")], PdPopup.prototype, "_dialog", void 0);
//#endregion
export { PdPopup };
